import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";

/**
 * LanguageSwitcher, compact dropdown for switching app language.
 * Renders the current flag + language code; opens a popover listing
 * every supported locale. Selection is persisted via i18next's
 * localStorage detector cache.
 *
 * Drop into the header / footer / settings modal:
 *    <LanguageSwitcher />               // default: header style
 *    <LanguageSwitcher variant="footer" /> // condensed footer style
 */
const LanguageSwitcher = ({ variant = "header" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const select = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className={
          variant === "footer"
            ? "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-white/60 hover:text-white/90 hover:bg-white/5"
            : "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/10 border border-white/10"
        }
        aria-label="Change language"
        data-testid="language-switcher-trigger"
      >
        <Globe size={14} />
        <span>{current.flag}</span>
        <span className="font-bold uppercase">{current.code}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-44 rounded-xl bg-slate-900/95 border border-white/10 shadow-2xl backdrop-blur-md py-1 z-50"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => select(l.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/5 transition-colors ${
                l.code === current.code ? "text-white" : "text-white/60"
              }`}
              data-testid={`language-switcher-option-${l.code}`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="flex-1 font-bold">{l.label}</span>
              {l.code === current.code && <Check size={14} className="text-emerald-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
